import type { SessionContact } from "./protocol";

/** One row of the contact list: a session, and how deeply it is nested under a parent. */
export interface SessionRow {
  session: SessionContact;
  /** 0 for a top-level session, 1 for a child shown under its parent. */
  depth: number;
}

/**
 * Lay the contact list out so a repository's worktrees sit under the checkout they branched from.
 *
 * Nesting is only ever one deep. A child of a child would be a tree the list has no room to draw and no
 * user has asked for; the relation itself is between a worktree and the main checkout, which is flat.
 *
 * A child whose parent is not in the list — never opened, archived, or on another machine — is shown at the
 * top level rather than hidden. Losing sight of a live session because its parent is missing would be worse
 * than showing it unnested, which is exactly how it appeared before any of this existed.
 */
export function sessionRows(sessions: SessionContact[]): SessionRow[] {
  // Keyed by pairing AND session: the list spans every pairing at once, and a session id is only unique
  // within one of them — so an id shared by two machines would otherwise adopt a stranger's session.
  const key = (pairingId: string, sessionId: string) => `${pairingId}/${sessionId}`;
  const byKey = new Map(sessions.map((s) => [key(s.pairingId, s.sessionId), s]));
  const parentOf = (s: SessionContact): SessionContact | undefined => {
    if (s.relationship !== "child" || !s.parentSessionId) return undefined;
    const parent = byKey.get(key(s.pairingId, s.parentSessionId));
    // A parent that is itself a child would make the row two deep, so it is not treated as one.
    return parent && parent.sessionId !== s.sessionId && parent.relationship !== "child" ? parent : undefined;
  };

  const children = new Map<string, SessionContact[]>();
  const top: SessionContact[] = [];
  for (const s of sessions) {
    const parent = parentOf(s);
    if (!parent) {
      top.push(s);
      continue;
    }
    const parentKey = key(parent.pairingId, parent.sessionId);
    const siblings = children.get(parentKey);
    if (siblings) siblings.push(s);
    else children.set(parentKey, [s]);
  }

  // A family sits where its highest-ranked member would, so a child's new message lifts its parent with it.
  const rows: SessionRow[] = [];
  const placed = new Set<string>();
  for (const s of sessions) {
    const head = parentOf(s) ?? s;
    const headKey = key(head.pairingId, head.sessionId);
    if (placed.has(headKey)) continue;
    placed.add(headKey);
    rows.push({ session: head, depth: 0 });
    for (const child of children.get(headKey) ?? []) {
      rows.push({ session: child, depth: 1 });
    }
  }
  return rows;
}

/** One entry of the session list: a session row, or the fold standing in for a family's quiet worktrees. */
export type ListEntry =
  | { kind: "session"; row: SessionRow }
  | { kind: "fold"; parentKey: string; hidden: number; expanded: boolean };

/**
 * Fold away a family's quiet worktrees: under each family, the children that are offline and not kept by
 * `keep` (unread messages, the open chat) hide behind one fold entry. A family whose parent key
 * (`pairingId/sessionId`) is in `expanded` lists every child and keeps the fold as the way to close it.
 */
export function foldQuietChildren(
  rows: SessionRow[],
  expanded: ReadonlySet<string>,
  keep: (s: SessionContact) => boolean,
): ListEntry[] {
  const out: ListEntry[] = [];
  let i = 0;
  while (i < rows.length) {
    const head = rows[i++];
    out.push({ kind: "session", row: head });
    const children: SessionRow[] = [];
    while (i < rows.length && rows[i].depth > 0) children.push(rows[i++]);
    const parentKey = `${head.session.pairingId}/${head.session.sessionId}`;
    const open = expanded.has(parentKey);
    const quiet = new Set(children.filter((c) => !c.session.online && !c.session.asleep && !keep(c.session)));
    for (const c of children) if (open || !quiet.has(c)) out.push({ kind: "session", row: c });
    if (quiet.size > 0) out.push({ kind: "fold", parentKey, hidden: quiet.size, expanded: open });
  }
  return out;
}

const SEPARATORS = "/-_.";

/**
 * The part of each worktree's branch that tells it apart from its siblings, keyed by `pairingId/sessionId`.
 *
 * Siblings are the worktrees recorded under the same parent, whether or not that parent is listed. The prefix
 * they all share is dropped up to its last separator and replaced by "…"; a worktree with no sibling keeps
 * its whole branch.
 */
export function distinctBranches(sessions: SessionContact[]): Map<string, string> {
  const families = new Map<string, SessionContact[]>();
  for (const s of sessions) {
    if (s.relationship !== "child" || !s.branch) continue;
    const fam = `${s.pairingId}/${s.parentSessionId}`;
    const members = families.get(fam);
    if (members) members.push(s);
    else families.set(fam, [s]);
  }
  const out = new Map<string, string>();
  for (const members of families.values()) {
    const branches = members.map((m) => m.branch);
    let cut = 0;
    if (new Set(branches).size > 1) {
      const lcp = branches.reduce((a, b) => {
        let n = 0;
        while (n < a.length && n < b.length && a[n] === b[n]) n++;
        return a.slice(0, n);
      });
      for (let n = lcp.length - 1; n >= 0; n--) {
        if (SEPARATORS.includes(lcp[n])) {
          cut = n + 1;
          break;
        }
      }
      if (branches.some((b) => b.length <= cut)) cut = 0;
    }
    for (const m of members) out.set(`${m.pairingId}/${m.sessionId}`, cut ? `…${m.branch.slice(cut)}` : m.branch);
  }
  return out;
}

/** Shorten `text` to at most `max` characters by cutting from the middle, keeping more of the end than the start. */
export function middleEllipsis(text: string, max: number): string {
  if (text.length <= max) return text;
  const head = Math.max(1, Math.floor((max - 1) / 3));
  return `${text.slice(0, head)}…${text.slice(text.length - (max - 1 - head))}`;
}
